import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { InfoOutlined, DirectionsRun, Restaurant } from "@mui/icons-material";
import { ExtensionSummaryProps } from "@/components/product-detail-page/itinerary-section/types";
import { useTranslations } from "next-intl";
import ActivityLevelIndicator from "@/components/common/activity-level-indicator/activity-level-indicator";
import Image from "next/image";

const ExtensionSummary = ({
  extensionStyle,
  meals,
  activityLevelDescription,
  activityLevelNumber,
  extensionStyleProps,
  description,
  testIdPrefix,
  handleOpenExtensionStyles,
  handleOpenActivityLevel
}: ExtensionSummaryProps) => {
  const t = useTranslations("PDP");

  const selectedExtension = extensionStyleProps.find(
    (extension) => extension.name.toLowerCase() === extensionStyle.toLowerCase()
  );

  return (
    <Stack
      sx={{
        padding: { xs: "0 2.4rem", md: "2.4rem 4.8rem" },
        gap: "3.2rem"
      }}
    >
      <Stack
        sx={{
          gap: "1.6rem"
        }}
      >
        {/* Activity Level */}
        {activityLevelDescription && activityLevelNumber && (
          <Stack
            sx={{
              fontSize: "1.6rem",
              fontWeight: 500,
              lineHeight: 1.5,
              display: "flex",
              gap: "0.8rem",
              flexDirection: "row",
              alignItems: { xs: "flex-start", sm: "center" },
              cursor: "pointer",
              width: "fit-content"
            }}
            onClick={() => handleOpenActivityLevel(activityLevelNumber)}
          >
            <DirectionsRun sx={{ height: "2.4rem", width: "2.4rem" }} />
            <Typography
              component="span"
              sx={{ fontWeight: 700 }}
              data-testid="activity-level-label"
            >
              {t("Activity Level")}:{" "}
            </Typography>
            <Stack
              sx={{
                flexDirection: { xs: "column", sm: "row" },
                gap: "0.8rem",
                alignItems: { xs: "flex-start", sm: "center" }
              }}
            >
              <Box
                component="span"
                sx={{ display: "flex", alignItems: "center", gap: "6px" }}
                data-testid={`${testIdPrefix}_extension-summary_activity-level`}
              >
                {activityLevelDescription}
                <InfoOutlined sx={{ height: "20px", width: "20px", marginBottom: "2px" }} />
              </Box>
              <ActivityLevelIndicator
                activityLevelNumber={activityLevelNumber}
                size="small"
                testIdPrefix={testIdPrefix}
              />
            </Stack>
          </Stack>
        )}

        {/* Meals */}
        {meals && (
          <Stack
            sx={{
              fontSize: "1.6rem",
              fontWeight: 500,
              lineHeight: 1.5,
              display: "flex",
              gap: "0.8rem",
              flexDirection: "row",
              alignItems: { xs: "flex-start", sm: "center" },
              width: "fit-content"
            }}
          >
            <Restaurant sx={{ height: "2.4rem", width: "2.4rem" }} />
            <Typography component="span" sx={{ fontWeight: 700 }} data-testid="meals-info-label">
              {/* count: 2 to get plural of Meal */}
              {t("Meals", { count: 2 })}:{" "}
            </Typography>
            <Typography data-testid="meals-info">{meals}</Typography>
          </Stack>
        )}
        {/* Extension Style */}
        {extensionStyle && (
          <Stack
            sx={{
              display: "flex",
              gap: "0.8rem",
              flexDirection: "row",
              alignItems: "center",
              cursor: "pointer",
              width: "fit-content"
            }}
            onClick={() => handleOpenExtensionStyles(extensionStyle)}
          >
            {selectedExtension?.imageUrl && selectedExtension?.imageAltTag && (
              <Image
                src={selectedExtension?.imageUrl}
                alt={selectedExtension?.imageAltTag}
                height={24}
                width={24}
              />
            )}
            <Box
              sx={{
                fontSize: "1.6rem",
                fontWeight: 500,
                lineHeight: 1.5,
                display: "flex",
                gap: "6px"
              }}
            >
              <Typography
                component="span"
                sx={{ fontWeight: 700 }}
                data-testid="extension-style-label"
              >
                {t("Extension Style")}:{" "}
              </Typography>
              <Typography
                component="span"
                sx={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}
                data-testid="extension-style"
              >
                {extensionStyle}
                <InfoOutlined sx={{ height: "20px", width: "20px", marginBottom: "2px" }} />
              </Typography>
            </Box>
          </Stack>
        )}
      </Stack>

      {/* Description */}
      <Typography data-testid="extension-style-description">{description}</Typography>
    </Stack>
  );
};

export default ExtensionSummary;
